// Fix AN (2026-10-23): textos y opciones de los mensajes automáticos de WhatsApp.
// Fix AQ (2026-10-23): las reglas también pueden mandar por SMS (texto propio con datos entre llaves).

export const TRIGGERS = [
  { key: "on_booking", label: "Al reservar un turno", units: ["minutes", "hours"], defaultValue: 0, defaultUnit: "minutes", params: ["nombre", "tratamiento", "fecha", "hora", "instrucciones"] },
  { key: "before_appointment", label: "Antes del turno", units: ["hours", "days"], defaultValue: 24, defaultUnit: "hours", params: ["nombre", "tratamiento", "fecha", "hora", "instrucciones"] },
  { key: "after_treatment", label: "Después de un tratamiento", units: ["hours", "days"], defaultValue: 1, defaultUnit: "days", params: ["nombre", "tratamiento", "fecha", "instrucciones"] },
  { key: "treatment_due", label: "Cuando vence el efecto del tratamiento (recurrencia)", units: ["days"], defaultValue: 3, defaultUnit: "days", params: ["nombre", "tratamiento", "fecha"] },
];

export const triggerOf = (key) => TRIGGERS.find((t) => t.key === key) || TRIGGERS[0];

const UNIT_LABELS = { minutes: ["minuto", "minutos"], hours: ["hora", "horas"], days: ["día", "días"] };
export const unitLabel = (unit, value) => (UNIT_LABELS[unit] || [unit, unit])[Number(value) === 1 ? 0 : 1];

/** Frase que describe cuándo se manda una regla. */
export function describeRule(rule) {
  const n = Number(rule.offset_value);
  const amount = `${n} ${unitLabel(rule.offset_unit, n)}`;
  if (rule.trigger === "on_booking") return n === 0 ? "Apenas se reserva el turno" : `${amount} después de reservar el turno`;
  if (rule.trigger === "before_appointment") return `${amount} antes del turno`;
  if (rule.trigger === "after_treatment") return `${amount} después de registrar un tratamiento`;
  if (rule.when === "before") return n === 0 ? "El día que vence el efecto del tratamiento" : `${amount} antes de que venza el efecto del tratamiento`;
  return n === 0 ? "El día que vence el efecto del tratamiento" : `${amount} después de que venció el efecto del tratamiento`;
}

export const SMS_MAX_CHARS = 600;
// Datos de ejemplo para ver cómo queda el texto de un SMS.
const SAMPLE = {
  es: { nombre: "Ana", tratamiento: "Botox", fecha: "27 de octubre", hora: "15:00" },
  en: { nombre: "Ana", tratamiento: "Botox", fecha: "October 27", hora: "3:00 PM" },
};
// Fix AV (2026-10-27): {instrucciones} es lo que el tratamiento tiene cargado en la Base de conocimiento.
const SAMPLE_INSTRUCTIONS = {
  before: "Llegá con la piel limpia y sin maquillaje, y evitá el alcohol las 24 horas previas.",
  after: "Evitá el sol directo y el ejercicio intenso por 24 horas, y usá protector solar.",
};

/** Motivo por el que el texto de un SMS no sirve (vacío si sirve): mismas reglas que el servidor. */
export function smsProblem(text, params) {
  const value = String(text || "").trim();
  if (!value) return "";
  if (value.length > SMS_MAX_CHARS) return `El texto es demasiado largo (máximo ${SMS_MAX_CHARS} caracteres).`;
  const names = [...value.matchAll(/\{([a-z_]+)\}/g)].map((m) => m[1]);
  const bad = [...new Set(names.filter((n) => !params.includes(n)))];
  if (bad.length) return `Este tipo de mensaje no completa ${bad.map((b) => `{${b}}`).join(", ")}. Usá solo: ${params.map((p) => `{${p}}`).join(", ")}.`;
  if (/[{}]/.test(value.replace(/\{([a-z_]+)\}/g, ""))) return "Hay una llave { o } suelta: los datos van entre llaves y sin espacios, por ejemplo {nombre}.";
  return "";
}

/** El texto con datos de ejemplo, como lo vería la paciente. */
export const smsPreviewText = (text, language, trigger) => {
  const sample = { ...(SAMPLE[language] || SAMPLE.es), instrucciones: trigger === "after_treatment" ? SAMPLE_INSTRUCTIONS.after : SAMPLE_INSTRUCTIONS.before };
  return String(text || "").replace(/\{([a-z_]+)\}/g, (all, name) => sample[name] ?? all);
};

export const CHANNELS = { whatsapp: "WhatsApp", sms: "SMS" };

// Textos sugeridos para las reglas de SMS. Sin tildes en á, í, ó, ú (con una sola, cada SMS pasa a 70 caracteres y sale más caro).
export const SUGGESTED_SMS = {
  on_booking: "Hola {nombre}, tu turno de {tratamiento} fue agendado para el {fecha} a las {hora}. Si necesitas cambiarlo, respondé REAGENDAR. Strani Just Because. Respondé STOP para dejar de recibir SMS.",
  before_appointment: "Hola {nombre}, te recordamos tu turno de {tratamiento} el {fecha} a las {hora}. Respondé CONFIRMO para confirmarlo o REAGENDAR para cambiarlo. Strani Just Because. Respondé STOP para dejar de recibir SMS.",
  after_treatment: "Hola {nombre}, te escribimos para saber como te sentis después de tu {tratamiento} del {fecha}. Si tenés alguna consulta o molestia, respondé a este mensaje. Strani Just Because. Respondé STOP para dejar de recibir SMS.",
  treatment_due: "Hola {nombre}, segun tu ultima sesion de {tratamiento}, tu proximo control corresponde alrededor del {fecha}. Si querés coordinar el turno, respondé a este mensaje. Strani Just Because. Respondé STOP para dejar de recibir SMS.",
};

export const LOG_STATUS = {
  sent: ["Enviado", "bg-[#E3EDE1] text-[#5B7A54]"],
  failed: ["Con error", "bg-[#F6DEDC] text-[#A4473C]"],
  skipped: ["No se mandó", "bg-muted text-muted-foreground"],
  sending: ["Enviando", "bg-[#F4E4D6] text-[#B5793A]"],
};

export const ANSWERED = {
  confirmed: "Confirmó el turno",
  change: "Pidió cambiar o cancelar",
  inactive: "Quiso confirmar un turno que ya no estaba activo",
};

// Textos para crear en Conversaciones > Plantillas (Utilidad). Son avisos sobre un turno o
// tratamiento puntual de la persona: sin tono de promoción, para que Meta los apruebe como Utilidad.
export const SUGGESTED_TEMPLATES = [
  {
    key: "confirmacion",
    title: "Confirmación de turno",
    useFor: "Al reservar un turno",
    body: "Hola {{nombre}}, tu turno de {{tratamiento}} quedó agendado para el {{fecha}} a las {{hora}}. Si necesitás cambiarlo, respondé a este mensaje.",
    footer: "Strani Just Because",
    buttons: [],
  },
  {
    key: "recordatorio",
    title: "Recordatorio de turno",
    useFor: "Antes del turno",
    body: "Hola {{nombre}}, te recordamos tu turno de {{tratamiento}} el {{fecha}} a las {{hora}}. ¿Nos confirmás tu asistencia?",
    footer: "Strani Just Because",
    buttons: ["Confirmo", "Necesito reagendar"],
  },
  {
    key: "seguimiento",
    title: "Seguimiento de tratamiento",
    useFor: "Después de un tratamiento",
    body: "Hola {{nombre}}, te escribimos para saber cómo te sentís después de tu tratamiento de {{tratamiento}} del {{fecha}}. Si tenés alguna consulta o molestia, respondé a este mensaje y te ayudamos.",
    footer: "Strani Just Because",
    buttons: [],
  },
  {
    key: "instrucciones_antes",
    title: "Instrucciones antes del turno",
    useFor: "Al reservar un turno o antes del turno",
    body: "Hola {{nombre}}, tu turno de {{tratamiento}} es el {{fecha}} a las {{hora}}. Para prepararte, tené en cuenta lo siguiente: {{instrucciones}} Si tenés alguna duda, respondé a este mensaje.",
    footer: "Strani Just Because",
    buttons: [],
  },
  {
    key: "instrucciones_despues",
    title: "Cuidados después del tratamiento",
    useFor: "Después de un tratamiento",
    body: "Hola {{nombre}}, gracias por venir a tu sesión de {{tratamiento}} del {{fecha}}. Los cuidados que te recomendamos seguir: {{instrucciones}} Si tenés alguna molestia o consulta, respondé a este mensaje.",
    footer: "Strani Just Because",
    buttons: [],
  },
  {
    key: "recurrencia",
    title: "Aviso de próximo control",
    useFor: "Cuando vence el efecto del tratamiento",
    body: "Hola {{nombre}}, según tu última sesión de {{tratamiento}}, tu próximo control corresponde alrededor del {{fecha}}. Si querés coordinar el turno, respondé a este mensaje.",
    footer: "Strani Just Because",
    buttons: [],
  },
];
